import { useField, useFormikContext } from "formik";
import React from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicSinglelineTextInput } from "@gaming/xds-react-js";

import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FieldHeader, FieldStateProps } from "../FieldHeader";
import { TextField } from "../TextField";
import { useSetFieldTouched } from "../useSetFieldTouched";
import * as styles from "./PhoneInputField.module.css";

type PhoneInputFieldProps = Readonly<{
  name: string;
  title: string;
  type?: "number" | "string";
  isRequiredField?: boolean;
  countryCode?: string;
  phoneNumberRegex?: string;
}> &
  FieldStateProps;

/** Extracts the number portion (without country code prefix) from the stored value */
const getDisplayValue = (storedValue: string | undefined, countryCode: string): string => {
  if (!storedValue) {
    return "";
  }
  const prefix = `+${countryCode} `;
  if (storedValue.startsWith(prefix)) {
    return storedValue.slice(prefix.length).replace(/\s/g, "");
  }
  // Strip any +{digits} prefix generically (handles mismatched country codes)
  const withoutAnyPrefix = storedValue.replace(/^\+\d+\s*/, "");
  return withoutAnyPrefix.replace(/\D/g, "");
};

export const PhoneInputField = (props: PhoneInputFieldProps) => {
  const { name, title, type, disabled, isSaving, isRequiredField, countryCode } = props;

  const { setFieldValue } = useFormikContext();
  const [field] = useField(props);

  const [localDisplay, setLocalDisplay] = React.useState(() =>
    getDisplayValue(field.value, countryCode ?? "")
  );

  const lastSetFieldValueRef = React.useRef<string | undefined>(field.value);
  const prevCountryCodeRef = React.useRef<string | undefined>(countryCode);

  React.useEffect(() => {
    const countryChanged = prevCountryCodeRef.current !== countryCode;
    const valueChangedExternally = field.value !== lastSetFieldValueRef.current;

    if (countryChanged || valueChangedExternally) {
      const display = getDisplayValue(field.value, countryCode ?? "");
      setLocalDisplay(display);

      if (countryChanged && countryCode && display) {
        const digits = display.replace(/\D/g, "");
        let newStored: string;
        if (countryCode === "1" && digits.length >= 3) {
          newStored = `+${countryCode} ${digits.slice(0, 3)} ${digits.slice(3)}`;
        } else {
          newStored = digits.length > 0 ? `+${countryCode} ${digits}` : "";
        }
        lastSetFieldValueRef.current = newStored;
        setFieldValue(name, newStored);
      } else {
        lastSetFieldValueRef.current = field.value;
      }
      prevCountryCodeRef.current = countryCode;
    }
  }, [field.value, countryCode, name, setFieldValue]);

  useSetFieldTouched(field.name);

  if (disabled && !countryCode) {
    return (
      <TextField
        name={name}
        title={title}
        isRequiredField={isRequiredField}
        disabled={true}
        isSaving={isSaving}
      />
    );
  }

  if (countryCode) {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/[^0-9]/g, "");
      setLocalDisplay(raw);

      const digits = raw.replace(/\D/g, "");

      let storedValue: string;
      if (countryCode === "1" && digits.length >= 3) {
        const areaCode = digits.slice(0, 3);
        const number = digits.slice(3);
        storedValue = `+${countryCode} ${areaCode} ${number}`;
      } else {
        storedValue = digits.length > 0 ? `+${countryCode} ${digits}` : "";
      }

      lastSetFieldValueRef.current = storedValue;
      setFieldValue(name, storedValue);
    };

    return (
      <Row className={commonStyles.container}>
        <Column>
          <FieldHeader
            title={title}
            isRequiredField={isRequiredField}
            htmlFor={name}
            titleClassName={commonStyles.textFieldLabel}
          />
          <div className={styles.phoneInputRow}>
            <span className={styles.countryCodePrefix}>+{countryCode}</span>
            {isSaving || disabled ? (
              <div className={styles.phoneInput}>
                <BasicSinglelineTextInput
                  aria-required="true"
                  className={commonStyles.textField}
                  data-testid={name}
                  disabled={true}
                  id={name}
                  inputClassName={commonStyles.textFieldText}
                  maxLength={15}
                  name={name}
                  onBlur={field.onBlur}
                  type={type}
                  value={getDisplayValue(field.value, countryCode)}
                />
              </div>
            ) : (
              <div className={styles.phoneInput}>
                <BasicSinglelineTextInput
                  aria-required="true"
                  className={commonStyles.textField}
                  data-testid={name}
                  disabled={false}
                  id={name}
                  inputClassName={commonStyles.textFieldText}
                  maxLength={15}
                  name={name}
                  onChange={handleChange}
                  onBlur={field.onBlur}
                  type={type}
                  value={localDisplay}
                />
              </div>
            )}
          </div>
          <FieldErrorMessage fieldName={name} />
        </Column>
      </Row>
    );
  }

  return (
    <TextField
      name={name}
      title={title}
      type={type}
      isRequiredField={isRequiredField}
      disabled={disabled}
      isSaving={isSaving}
      maxLength={15}
    />
  );
};
